import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as amplitude from '@amplitude/analytics-browser';

import type {
  IOnboardingBannerProps,
  IUseOnboardingConfigParams,
  TOnboardingStatus,
} from '@app/dashboard/types/innerLayout/home/onboarding';
import type { TActiveModal } from '@app/dashboard/types/innerLayout/home/onboarding/modals';
import {
  ONBOARDING_STATES,
  SPECIAL_HANDLERS,
  MODAL_TYPES,
} from '@app/dashboard/constants/Home/onboarding';
import { LOCAL_STORAGE_KEYS } from '@app/dashboard/constants/local-storage-keys';
import { INNER_ROUTES } from '@app/dashboard/constants/inner-routes';
import {
  ONBOARDING_BANNER_EVENTS,
  ONBOARDING_BANNER_PARAMS,
  ONBOARDING_BANNER_VALUES,
} from '@app/dashboard/constants/tracking_events/home';
import {
  addToLocalStorage,
  getFromLocalStorage,
} from '@app/dashboard/utils/manageLocalStorage';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { useLangStore } from '@app/dashboard/store/lang';

import { buildBannerConfigMap } from '../utils';

export const useOnboardingConfig = ({
  bannerData,
}: IUseOnboardingConfigParams) => {
  const [isLoading, setIsLoading] = useState(false);
  const [activeModal, setActiveModal] = useState<TActiveModal>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { setIsLive } = useTestLiveModeStore();
  const { lang } = useLangStore();

  const country = getFromLocalStorage(LOCAL_STORAGE_KEYS.COUNTRY);
  const userId = getFromLocalStorage(LOCAL_STORAGE_KEYS.USER_ID);
  const merchantId = getFromLocalStorage(LOCAL_STORAGE_KEYS.MERCHANT_ID);
  const phone = getFromLocalStorage(LOCAL_STORAGE_KEYS.PHONE);

  const trackBannerEvent = (eventName: string) => {
    const { status, message } = bannerData || {};

    amplitude.track(eventName, {
      [ONBOARDING_BANNER_PARAMS.STATUS]: status,
      [ONBOARDING_BANNER_PARAMS.MESSAGE]: message,
      [ONBOARDING_BANNER_PARAMS.MERCHANT_ID]: merchantId,
      [ONBOARDING_BANNER_PARAMS.PHONE_NUMBER]: phone,
      [ONBOARDING_BANNER_PARAMS.LANGUAGE]: lang,
      [ONBOARDING_BANNER_PARAMS.COUNTRY]: country,
      [ONBOARDING_BANNER_PARAMS.PAGE_NAME]: ONBOARDING_BANNER_VALUES.PAGE_NAME,
      [ONBOARDING_BANNER_PARAMS.USER_ID]: userId,
    });
  };

  const handleOpenOnboardingUrl = async ({
    eventName,
    onboardingUrl = bannerData?.url,
    setIsWaiting = setIsLoading,
  }: {
    eventName?: string;
    onboardingUrl?: string;
    setIsWaiting: (waiting: boolean) => void;
  }) => {
    if (!onboardingUrl) return;

    if (eventName) trackBannerEvent(eventName);

    setIsWaiting(true);

    await new Promise((res) => setTimeout(res, 1000));

    setIsWaiting(false);

    window.open(bannerData?.url, '_self');
  };

  const handleOpenModal = (modal: TActiveModal, eventName: string) => {
    if (eventName) trackBannerEvent(eventName);
    setActiveModal(modal);
  };

  const handleCloseModal = () => {
    setActiveModal(null);
  };

  const handleNonEgyptLive = async (eventName: string) => {
    setIsLive(true);

    trackBannerEvent(eventName);

    await new Promise((res) => setTimeout(res, 25));

    navigate(INNER_ROUTES.PAYMENT_INTEGRATIONS);
  };

  const handlers = {
    [ONBOARDING_STATES.PENDING_ONBOARDING]: handleOpenOnboardingUrl,
    [ONBOARDING_STATES.IN_ONBOARDING_PROCESS]: handleOpenOnboardingUrl,
    [ONBOARDING_STATES.UPDATES_REQUIRED]: () =>
      handleOpenModal(
        MODAL_TYPES.UPDATES_REQUIRED as TActiveModal,
        ONBOARDING_BANNER_EVENTS.VIEW_ISSUES_BTN_CLICK
      ),
    [ONBOARDING_STATES.LIVE]: handleNonEgyptLive,
    [SPECIAL_HANDLERS.MAIL_SUPPORT]: trackBannerEvent,
  };

  const onboardingConfig = useMemo(() => {
    if (!bannerData) return null;

    const lookupKey = bannerData.status || bannerData.message;

    if (!lookupKey) return null;

    const configMap = buildBannerConfigMap({
      t,
      country,
      isLoading,
      handlers,
    });

    const config = configMap[lookupKey as TOnboardingStatus] || null;

    return config as unknown as IOnboardingBannerProps;
  }, [bannerData, country, isLoading, t]);

  useEffect(() => {
    if (!bannerData) return;

    const { status } = bannerData || {};

    if (!status) return;

    const statusKey = status?.toLocaleLowerCase().split(' ').join('_');
    const bannerViewedKey = `banner_viewed_${statusKey}_${merchantId}`;
    const hasViewed = getFromLocalStorage(bannerViewedKey);

    if (!hasViewed) {
      trackBannerEvent(ONBOARDING_BANNER_EVENTS.ONBOARDING_BANNER_VIEWED);
      addToLocalStorage(bannerViewedKey, true);
    }
  }, [bannerData]);

  return {
    onboardingConfig,
    activeModal,
    handleCloseModal,
    handleOpenOnboardingUrl,
  };
};
